import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Plus } from "lucide-react";
import { useEffect } from "react";
import { Seo } from "@/seo/Seo";
import { StartBuild } from "@/components/site/primitives";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { LANDING_BY_SLUG } from "@/data/serviceLandings";
import { serviceSchema, faqSchema, breadcrumbSchema } from "@/seo/config";
import { trackEvent } from "@/lib/attribution";

const ease = [0.22, 1, 0.36, 1];
const reveal = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.75, ease },
};
const CONTAINER = "max-w-[1400px] mx-auto px-6 md:px-12";

export default function ServiceLanding() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const p = LANDING_BY_SLUG[slug];
  useEffect(() => {
    if (p) trackEvent("service_view", { service_page: `/${p.slug}`, slug: p.slug }, { once: `svc:${p.slug}` });
  }, [p]);
  if (!p) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
        <Seo title="Page not found | AIROTECH" path={`/${slug || ""}`} noindex />
        <h1 className="font-heading text-4xl text-white mb-4">Page not found</h1>
        <Link to="/services" className="mono-label text-cyan-300">View all services →</Link>
      </div>
    );
  }
  const crumbs = [{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: p.nav, to: `/${p.slug}` }];

  return (
    <div>
      <Seo
        title={p.metaTitle}
        description={p.metaDescription}
        path={`/${p.slug}`}
        type="website"
        schema={[serviceSchema(p), faqSchema(p.faqs), breadcrumbSchema(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />

      {/* Hero */}
      <section className="pt-10 md:pt-14 pb-16 md:pb-24">
        <div className={CONTAINER}>
          <motion.div {...reveal} className="mono-label text-cyan-400 mb-6">{p.kicker}</motion.div>
          <motion.h1 {...reveal} className="font-heading font-extrabold tracking-tighter leading-[0.98] text-4xl md:text-6xl max-w-4xl">
            {p.h1}
          </motion.h1>
          <motion.p {...reveal} className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed">{p.intro}</motion.p>
          <motion.div {...reveal} className="mt-10">
            <StartBuild testid="landing-start-build-btn" size="lg" label="Start Your Build" />
          </motion.div>
        </div>
      </section>

      {/* Value sections */}
      <section className="py-16 md:py-24 border-t border-[#131313]">
        <div className={`${CONTAINER} grid md:grid-cols-3 gap-8 md:gap-12`}>
          {p.sections.map((s, i) => (
            <motion.div key={s.h} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}>
              <div className="font-mono-ui text-cyan-400/60 text-sm mb-4">{String(i + 1).padStart(2, "0")}</div>
              <h2 className="font-heading text-2xl text-white tracking-tight">{s.h}</h2>
              <p className="mt-4 text-zinc-400 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 md:py-24 border-t border-[#131313]">
        <div className={CONTAINER}>
          <motion.h2 {...reveal} className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-10">What's included</motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {p.included.map((x) => (
              <motion.div key={x} {...reveal} className="flex items-start gap-3 py-3 border-b border-[#161616]">
                <Check className="h-5 w-5 mt-0.5 shrink-0 text-cyan-400" />
                <span className="text-zinc-300">{x}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs (AEO) */}
      <section className="py-16 md:py-24 border-t border-[#131313]">
        <div className={CONTAINER}>
          <motion.h2 {...reveal} className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-10">Frequently asked questions</motion.h2>
          <div className="max-w-3xl">
            {p.faqs.map((f) => (
              <motion.details key={f.q} {...reveal} className="group border-b border-[#161616] py-6">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="font-heading text-lg md:text-xl text-white">{f.q}</span>
                  <Plus className="h-5 w-5 shrink-0 text-cyan-400 transition-transform duration-300 group-open:rotate-45" />
                </summary>
                <p className="mt-4 text-zinc-400 leading-relaxed">{f.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* Related + CTA */}
      <section className="py-20 md:py-28 border-t border-[#131313]">
        <div className={CONTAINER}>
          <div className="mono-label text-zinc-600 mb-6">Related services</div>
          <div className="flex flex-wrap gap-3 mb-16">
            {p.related.map((r) => {
              const rp = LANDING_BY_SLUG[r];
              if (!rp) return null;
              return (
                <Link key={r} to={`/${r}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-[#232323] px-5 py-2.5 mono-label text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors">
                  {rp.nav}<ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              );
            })}
          </div>
          <div className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-10 md:p-16 text-center">
            <h2 className="font-serif-ed text-white text-4xl md:text-5xl leading-[1.05]">Ready to build it?</h2>
            <p className="mt-5 max-w-xl mx-auto text-zinc-400 text-lg">Start a guided product journey. Tell us what you want to build — we'll take it from there.</p>
            <div className="mt-9 flex justify-center">
              <StartBuild testid="landing-cta-start-build-btn" size="lg" label="Start Your Build" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
